import AcUnitIcon from '@mui/icons-material/AcUnit';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AddIcon from '@mui/icons-material/Add';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BlockIcon from '@mui/icons-material/Block';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import HistoryIcon from '@mui/icons-material/History';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import SettingsIcon from '@mui/icons-material/Settings';
import UTurnLeft from '@mui/icons-material/UTurnLeft';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import { useParties } from '../../contexts/PartiesContext';
import { AllocationRequests } from '../../pages/registry/allocationRequest/AllocationRequests';
import { AllowLists } from '../../pages/registry/allowList/AllowLists';
import { Burns } from '../../pages/registry/burn/Burns';
import { Clawbacks } from '../../pages/registry/clawback/Clawbacks';
import { Configurations } from '../../pages/registry/configuration/Configurations';
import { DenyLists } from '../../pages/registry/denyList/DenyLists';
import { FreezeHistory } from '../../pages/registry/freezes/FreezeHistory';
import { Freezes } from '../../pages/registry/freezes/Freezes';
import { Holdings } from '../../pages/registry/holdings/Holdings';
import { LedgerStatus } from '../../pages/registry/ledgerStatus/LedgerStatus';
import { Mints } from '../../pages/registry/mint/Mints';
import { Onboarding } from '../../pages/registry/onboarding/Onboarding';
import { PauseHistory } from '../../pages/registry/security/PauseHistory';
import { Transfers } from '../../pages/registry/transfer/Transfers';
import { TransferEnablementHistory } from '../../pages/registry/transferEnablement/TransferEnablementHistory';
import { ModuleRoute } from '../../routes/types';
import useAllowListRole from '../queries/registry/allowList/useAllowListRole';
import useDenyListRole from '../queries/registry/denyList/useDenyListRole';
import useComplianceService from '../queries/registry/onboarding/useComplianceService';
import useInstrumentSecurityControlService from '../queries/registry/onboarding/useInstrumentSecurityControlService';
import { useInboundAllocationRequestCount } from './useInboundAllocationRequests';

const sidebarEntries: ModuleRoute[] = [
  {
    label: 'Onboarding',
    path: 'onboarding',
    element: <Onboarding />,
    icon: <AdminPanelSettingsIcon fontSize="small" />,
  },
];

const configurationEntry: ModuleRoute = {
  label: 'Configurations',
  path: 'configurations',
  icon: <SettingsIcon fontSize="small" />,
  element: <Configurations />,
};

const holderEntries: ModuleRoute[] = [
  {
    label: 'Holdings',
    path: 'holdings',
    element: <Holdings />,
    icon: <AccountBalanceWalletIcon fontSize="small" />,
  },
  {
    label: 'Transfers',
    path: 'transfers',
    element: <Transfers />,
    icon: <CompareArrowsIcon fontSize="small" />,
  },
  { label: 'Mints', path: 'mints', element: <Mints />, icon: <AddIcon fontSize="small" /> },
  { label: 'Burns', path: 'burns', element: <Burns />, icon: <WhatshotIcon fontSize="small" /> },
  {
    label: 'Allocation Requests',
    path: 'allocationRequest',
    element: <AllocationRequests />,
    icon: <PlaylistAddIcon />,
    getNotificationHook: useInboundAllocationRequestCount,
  },
];

const operatorEntries: ModuleRoute[] = [
  {
    label: 'Ledger Status',
    path: 'ledger-status',
    icon: <ImportExportIcon fontSize="small" />,
    element: <LedgerStatus />,
  },
];

const securityEntries: ModuleRoute[] = [
  {
    label: 'Pause History',
    path: 'pause-history',
    icon: <HistoryIcon fontSize="small" />,
    element: <PauseHistory />,
  },
];

const registrarEntries: ModuleRoute[] = [
  {
    label: 'Clawbacks',
    path: 'clawbacks',
    icon: <UTurnLeft fontSize="small" />,
    element: <Clawbacks />,
  },
  {
    label: 'Freezes',
    path: 'freezes',
    icon: <AcUnitIcon fontSize="small" />,
    element: <Freezes />,
  },
  {
    label: 'Freeze History',
    path: 'freeze-history',
    icon: <HistoryIcon fontSize="small" />,
    element: <FreezeHistory />,
  },
  {
    label: 'Transfer Enablement',
    path: 'transfer-enablement-history',
    icon: <HistoryIcon fontSize="small" />,
    element: <TransferEnablementHistory />,
  },
];

export const useRegistryEntries = () => {
  const complianceService = useComplianceService();
  const instrumentSecurityControlService = useInstrumentSecurityControlService();
  const { isRegistrar, mode: allowListMode } = useAllowListRole();
  const { mode: denyListMode } = useDenyListRole();
  const { operator, party } = useParties();

  const isCompliance = !!complianceService.data;
  const isInstrumentSecurityControl =
    instrumentSecurityControlService.data?.payload.instrumentSecurityControl === party;
  const isOperator = operator === party;

  let entries = sidebarEntries;
  if (isOperator || isRegistrar || isCompliance || isInstrumentSecurityControl) {
    entries = entries.concat([configurationEntry]).concat(holderEntries);
  } else {
    entries = entries.concat(holderEntries);
  }

  if (isInstrumentSecurityControl) {
    entries = entries.concat(securityEntries);
  }

  if (allowListMode) {
    entries = entries.concat([
      {
        label: allowListMode === 'registrar' ? 'Master Allow List' : 'Allow Lists',
        path: 'allow-lists',
        element: <AllowLists mode={allowListMode} />,
        icon: <ListAltIcon fontSize="small" />,
      },
    ]);
  }

  if (denyListMode) {
    entries = entries.concat([
      {
        label: 'Deny Lists',
        path: 'deny-lists',
        element: <DenyLists />,
        icon: <BlockIcon fontSize="small" />,
      },
    ]);
  }

  if (isRegistrar) {
    entries = entries.concat(registrarEntries);
  }

  if (isOperator) {
    entries = entries.concat(operatorEntries);
  }
  return entries;
};
